package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.workspace.InviteLinkDto
import co.maytes.standupbot.shared.dto.workspace.MemberStatusDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration

object MembersCopy {
    const val NONE = "–"
    const val PEOPLE_DESCRIPTION =
        "Personal invites expire after 7 days. Disabled people can't sign in, and their past updates stay in the history."
    const val LINKS_DESCRIPTION = "Anyone with a link joins as the role you pick, after signing in with Slack."
    const val NO_MEMBERS = "No members yet. Invite a teammate so they can join standups and contribute their Linear activity."
    const val NO_MATCH = "No members match your search. Try a different term."
    const val ONLY_YOU = "It's just you so far. Invite teammates so they can join standups."
    const val NO_LINKS = "No invite links yet. Create one to let a whole team join."
    const val LINKS_ADMINS_ONLY = "Only owners and admins can manage invite links."
    const val LAST_OWNER_HINT = "A workspace needs at least one owner."

    data class Subtitle(val strong: String, val rest: String)

    fun roleLabel(role: RoleDto): String =
        when (role) {
            RoleDto.OWNER -> "Owner"
            RoleDto.ADMIN -> "Admin"
            RoleDto.MEMBER -> "Member"
            RoleDto.VIEWER -> "Viewer"
        }

    fun subtitle(members: List<WorkspaceMemberDto>, now: Instant): Subtitle {
        val active = members.count { it.status == MemberStatusDto.ACTIVE }
        val invited = members.filter { it.status == MemberStatusDto.INVITED }
        val expired = invited.count { it.inviteExpiresAt?.let { expiresAt -> expiresAt <= now } == true }
        val pending = invited.size - expired
        val strong =
            when (active) {
                0 -> "No one"
                1 -> "1 person"
                else -> "$active people"
            }
        val access = if (active > 1) " have access" else " has access"
        val tail =
            when {
                pending > 0 && expired > 0 ->
                    ", ${invites(pending)} ${isOrAre(pending)} pending and $expired ${hasOrHave(expired)} expired."
                pending > 0 -> " and ${invites(pending)} ${isOrAre(pending)} pending."
                expired > 0 -> " and ${invites(expired)} ${hasOrHave(expired)} expired."
                else -> "."
            }
        return Subtitle(strong = strong, rest = access + tail)
    }

    fun joined(member: WorkspaceMemberDto, zone: TimeZone = TimeZone.currentSystemDefault()): String =
        member.joinedAt?.toLocalDateTime(zone)?.date?.toString() ?: NONE

    fun inviteLinkTitle(link: InviteLinkDto): String =
        link.label?.takeIf { it.isNotBlank() } ?: "${roleLabel(link.role)} link"

    fun inviteLinkDetail(link: InviteLinkDto, now: Instant): String = "${roleLabel(link.role)} · ${inviteLinkSummary(link, now)}"

    fun inviteLinkSummary(link: InviteLinkDto, now: Instant): String = "${usage(link)} · ${expiry(link.expiresAt, now)}"

    private fun usage(link: InviteLinkDto): String =
        link.maxUses?.let { "${link.useCount} of $it used" } ?: "${link.useCount} used"

    private fun expiry(expiresAt: Instant, now: Instant): String {
        val remaining = expiresAt - now
        val days = remaining.inWholeDays
        val hours = remaining.inWholeHours
        return when {
            remaining <= Duration.ZERO -> "expired"
            days >= 1 -> "expires in ${counted(days, "day")}"
            hours >= 1 -> "expires in ${counted(hours, "hour")}"
            else -> "expires in under an hour"
        }
    }

    private fun invites(count: Int): String = counted(count.toLong(), "invite")

    private fun counted(count: Long, noun: String): String = if (count == 1L) "1 $noun" else "$count ${noun}s"

    private fun isOrAre(count: Int): String = if (count == 1) "is" else "are"

    private fun hasOrHave(count: Int): String = if (count == 1) "has" else "have"
}
